/**
 * Self-contained label/color maps for GraphQL enum types shared between `mini/`
 * and `web/`. Enum types here are plain string-literal unions (not imported from
 * either app's generated `gql/graphql`), so this module has no dependency on any
 * GraphQL codegen output. Real generated enum values are always assignable into
 * these types without a cast, since an enum member is always a member of the
 * equivalent string-literal union (see `event/README.md`'s "widening" note for
 * the same convention applied to `@tc-app/shared/event`).
 */

export type SportCategory = 'TENNIS' | 'PICKLEBALL'
export type CouponCategory = 'FIXED_DISCOUNT' | 'FREE_SIGNUP' | 'FREE_SLOT'
export type GameFormat = 'FREEPLAY' | 'SINGLES' | 'DOUBLES' | 'ROTATING_DOUBLES'
export type EventStatus = 'CREATED' | 'STARTED' | 'COMPLETED' | 'CANCELLED' | 'ABORTED'
export type OrderCategory = 'SLOT_BOOKING' | 'EVENT_SIGNUP' | 'COURSE'
export type OrderStatus = 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'CLOSED'
export type PaymentChannel = 'WEPAY' | 'MEITUAN'
export type CourseEntitlementStatus = 'ACTIVE' | 'EXPIRED' | 'USED_UP' | 'CANCELLED'
export type InvoiceStatus = 'PENDING' | 'ISSUED'
export type InvoiceeType = 'PERSON' | 'ORGANIZATION'

export const SPORT_LABEL: Record<SportCategory, string> = {
  TENNIS: '网球',
  PICKLEBALL: '匹克球',
}

export const COUPON_CATEGORY_LABEL: Record<CouponCategory, string> = {
  FIXED_DISCOUNT: '固定折扣',
  FREE_SLOT: '免费订场',
  FREE_SIGNUP: '免费报名',
}

export const GAME_FORMAT_LABEL: Record<GameFormat, string> = {
  FREEPLAY: '畅打',
  SINGLES: '单打',
  DOUBLES: '双打',
  ROTATING_DOUBLES: '循环双打',
}

export const getFormatLabel = (format: GameFormat): string =>
  GAME_FORMAT_LABEL[format] ?? '比赛'

// Compact 2-way chip (畅打 vs 比赛) used where slot/order grids only need to
// distinguish freeplay from competitive events.
export const GAME_FORMAT_CHIP: Record<
  GameFormat,
  { label: string; color: 'primary' | 'secondary' }
> = {
  FREEPLAY: { label: '畅打', color: 'secondary' },
  SINGLES: { label: '比赛', color: 'primary' },
  DOUBLES: { label: '比赛', color: 'primary' },
  ROTATING_DOUBLES: { label: '比赛', color: 'primary' },
}

export const EVENT_STATUS_LABEL: Record<EventStatus, string> = {
  CREATED: '报名中',
  STARTED: '进行中',
  COMPLETED: '已完成',
  CANCELLED: '已取消',
  ABORTED: '已中止',
}

export const EVENT_STATUS_COLOR: Record<
  EventStatus,
  'default' | 'primary' | 'success' | 'error' | 'warning'
> = {
  CREATED: 'primary',
  STARTED: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'error',
  ABORTED: 'error',
}

export const ORDER_TYPE_LABEL: Record<OrderCategory, string> = {
  SLOT_BOOKING: '订场',
  EVENT_SIGNUP: '活动',
  COURSE: '课程',
}

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  PENDING: '待支付',
  CONFIRMED: '已支付',
  CANCELLED: '已取消',
  CLOSED: '已关闭',
}

export const PAYMENT_CHANNEL_LABEL: Record<PaymentChannel, string> = {
  WEPAY: '微信',
  MEITUAN: '美团',
}

export const ORDER_STATUS_CHIP: Record<
  OrderStatus,
  { label: string; color: 'success' | 'warning' | 'default' | 'error' }
> = {
  PENDING: { label: ORDER_STATUS_LABEL.PENDING, color: 'warning' },
  CONFIRMED: { label: ORDER_STATUS_LABEL.CONFIRMED, color: 'success' },
  CANCELLED: { label: ORDER_STATUS_LABEL.CANCELLED, color: 'default' },
  CLOSED: { label: ORDER_STATUS_LABEL.CLOSED, color: 'error' },
}

export const INVOICE_STATUS_CHIP: Record<
  InvoiceStatus,
  { label: string; color: 'success' | 'warning' }
> = {
  PENDING: { label: '待开票', color: 'warning' },
  ISSUED: { label: '已开票', color: 'success' },
}

export const INVOICEE_TYPE_LABEL: Record<InvoiceeType, string> = {
  PERSON: '个人',
  ORGANIZATION: '单位',
}

export const COURSE_ENTITLEMENT_STATUS_CHIP: Record<
  CourseEntitlementStatus,
  { label: string; color: 'success' | 'default' | 'error' | 'warning' }
> = {
  ACTIVE: { label: '有效', color: 'success' },
  EXPIRED: { label: '已过期', color: 'warning' },
  USED_UP: { label: '已用完', color: 'default' },
  CANCELLED: { label: '已取消', color: 'error' },
}
